import { logError } from '../../utils/logging';
import {
  logDiffOperations,
  logSuggestEdit,
  logSuggestEditWarn,
  logTextBaselineComparison,
  logWordList,
  textSnapshot,
} from '../../utils/suggestEditDebug';
import {
  computeCharDiff as computeSharedCharDiff,
  computeContiguousCharDiff as computeSharedContiguousCharDiff,
  computeWordDiff as computeSharedWordDiff,
  DiffOp,
} from '../../word/utils/TextDiff';
import { TrackChangesParser } from '../../word/utils/TrackChangesParser';
import { computePunctuationAffixes } from '../../word/utils/punctuationAffix';
import { isOurSideTrackChangesAuthor } from '../../utils/quickResponses';

/* global DOMParser, XMLSerializer, Node, Element, TreeWalker, NodeFilter */

/** Verbose generator logging disabled (was used for edit-mode reverse engineering). */
function log(..._args: unknown[]): void {}

/**
 * Represents a single character's position in the XML structure
 */
interface TextPositionMap {
  charIndex: number;           // Position in the plain text string
  run: Element;                // Parent w:r (run) element
  textNode: Node;              // The w:t text node containing this character
  offset: number;              // Character offset within the text node
  runProperties: Element | null; // w:rPr formatting properties for this run
  isFromOriginalIns: boolean;  // Whether this character comes from an original w:ins element
  originalInsElement: Element | null; // The innermost w:ins element containing this character
  /** Author on the innermost w:ins, or null when the character is not inside w:ins. */
  originalInsAuthor: string | null;
}

/**
 * Represents a diff operation (unchanged, deletion, or insertion)
 */
interface DiffOperation {
  type: 'unchanged' | 'deletion' | 'insertion';
  text: string;
  startPos: number;            // Start position in original text
  endPos: number;              // End position in original text
  /**
   * Drop text without emitting a w:del. Used in simple-edit mode when the deleted
   * characters originated from an existing w:ins in the baseline (i.e. a pending
   * insertion) — mirroring how Word silently removes such text when track changes
   * are on and the user backspaces over an insertion.
   */
  silentRemoval?: boolean;
}

/**
 * Track-changes generation behaves in one of two modes:
 *
 * - `'redline-insert'` (default): treats the baseline as the "original" document and
 *   emits a fresh redline (w:ins/w:del). Used by the AI engine when producing a
 *   suggestion overlay.
 *
 * - `'simple-edit'`: emulates a human editing the displayed text inside Word with
 *   track changes turned on. Deletions of characters that already live inside a
 *   prior w:ins block (pending insertions) silently shrink that block — no w:del
 *   is created. Deletions of original document text still produce w:del as normal.
 *   Insertions still produce w:ins under the configured author. Use this whenever
 *   the user manually edits a clause (AI suggestion or counterparty redline).
 */
export type TrackChangesGenerationMode = 'redline-insert' | 'simple-edit';

/**
 * How simple-edit computes diff ops (char granularity only).
 * - `contiguous`: one prefix/suffix hunk vs source accepted text (save / full replace).
 * - `incremental-char`: LCS char diff vs source accepted text (live typing like Word).
 */
export type SimpleEditDiffStrategy = 'contiguous' | 'incremental-char';

export interface GenerateTrackChangesOptions {
  /** Selects the generation semantics. Defaults to `'redline-insert'`. */
  mode?: TrackChangesGenerationMode;
  /** simple-edit char diffs only. Defaults to `contiguous`. */
  simpleEditDiffStrategy?: SimpleEditDiffStrategy;
  /**
   * DOM-synced accepted text for live incremental edits. Diff runs against this
   * (not OOXML extract) so caret coordinates match the editable HTML model.
   */
  diffBaselineText?: string;
  /**
   * Live-edit replace hint. When set and `simpleEditDiffStrategy === 'incremental-char'`,
   * the diff is built as a single delete of `diffBaselineText[start..end]` followed by a
   * single insert of `insertedText` — bypassing LCS char diff entirely. Used when the
   * user types/pastes over a selection so the result is one clean replacement rather
   * than per-character matches that can accidentally preserve original chars
   * (e.g. typing "סעיף חדש" over "עוד יתווסף" should not keep the original `ס`).
   */
  editSelectionHint?: {
    start: number;
    end: number;
    insertedText: string;
  };
}

interface TraverseContext {
  currentTextPos: number;
  changeId: number;
  authorName: string;
  date: string;
  processedInsElements: Map<Element, Element>;
  parentInsElement: Element | null;
  generationMode: TrackChangesGenerationMode;
  paragraphRoot: Element;
  /** Original counterparty w:ins being streamed as sibling blocks (simple-edit). */
  counterpartySplitSource: Element | null;
  openCounterpartyIns: Element | null;
}

/**
 * Robust XML track changes generator
 * Uses rule-based approach to generate Word OOXML with track changes
 */
export class RobustXmlTrackChangesGenerator {
  private static readonly W_NAMESPACE = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';

  /**
   * Main entry point - generates track changes XML
   * @param originalOoxml - Original Word OOXML from selection
   * @param newText - New plain text to overlay
   * @param authorName - Author name for track changes
   * @returns OOXML with w:ins and w:del elements
   */
  static generateTrackChangesXml(
    originalOoxml: string,
    newText: string,
    authorName: string = 'User',
    debugMeta?: { source?: string; clauseId?: string; clauseLabel?: string | number },
    diffGranularity: 'word' | 'char' = 'word',
    options?: GenerateTrackChangesOptions,
  ): string {
    try {
      log('[RobustXmlTrackChangesGenerator] Starting XML generation');
      log(`[RobustXmlTrackChangesGenerator] Original OOXML length: ${originalOoxml.length}`);
      log(`[RobustXmlTrackChangesGenerator] New text length: ${newText.length}`);

      logSuggestEdit('GENERATOR — BEFORE', {
        ...debugMeta,
        authorName,
        originalOoxmlLen: originalOoxml.length,
        newText: textSnapshot(newText),
        originalOoxmlHasRef: /REF|MERGEFORMAT|instrText/i.test(originalOoxml),
      });

      // Apply full namespace sanitization pipeline before any XML parsing:
      //   1. deduplicateXmlnsAttributes  — removes duplicate xmlns:ns1 (Windows round-trip bug)
      //   2. sanitizeUndeclaredNamespacePrefixes — injects/strips undeclared prefix attrs
      //      (e.g. w14:paraId without xmlns:w14 after Word strips full-doc namespace decls)
      //   3. sanitizeFieldReferencesInOoxml — removes REF/MERGEFORMAT noise (also calls 1+2)
      const step1 = TrackChangesParser.deduplicateXmlnsAttributes(originalOoxml);
      const step2 = TrackChangesParser.sanitizeUndeclaredNamespacePrefixes(step1);
      const hasFieldRefs = /REF|MERGEFORMAT|instrText/i.test(step2);
      if (hasFieldRefs) {
        log('[RobustXmlTrackChangesGenerator] REF/MERGEFORMAT/instrText detected — sanitizing OOXML (same as Editor / ParagraphXMLDisplay)');
      }

      const workingOoxml = hasFieldRefs
        ? TrackChangesParser.sanitizeFieldReferencesInOoxml(step2)
        : step2;

      logSuggestEdit('GENERATOR — after sanitize', {
        ...debugMeta,
        workingOoxmlLen: workingOoxml.length,
        sanitized: hasFieldRefs,
      });

      const generationMode: TrackChangesGenerationMode = options?.mode ?? 'redline-insert';
      const simpleEditDiffStrategy: SimpleEditDiffStrategy =
        options?.simpleEditDiffStrategy ?? 'contiguous';
      const isLiveIncremental =
        generationMode === 'simple-edit' && simpleEditDiffStrategy === 'incremental-char';
      const diffBaselineText = options?.diffBaselineText;

      // Step 1–2: Build accepted text + position map in one pass (no trailing global REF strip)
      const ooxmlAccepted = this.buildAcceptedTextAndPositionMap(workingOoxml);
      let originalText = ooxmlAccepted.text;
      let textMap = ooxmlAccepted.map;

      if (isLiveIncremental && diffBaselineText) {
        if (diffBaselineText !== originalText) {
          const aligned = this.alignTextMapToDomBaseline(
            diffBaselineText,
            originalText,
            textMap,
          );
          logSuggestEdit('GENERATOR — aligned text map to DOM baseline', {
            ...debugMeta,
            ooxmlTextLen: originalText.length,
            domBaselineLen: diffBaselineText.length,
            alignedMapLen: aligned.map.length,
          });
          originalText = aligned.text;
          textMap = aligned.map;
        }
      } else {
        const trimmedBaseline = this.trimLeadingTrailingWhitespaceFromTextMap(originalText, textMap);
        if (trimmedBaseline.trimmedCount > 0) {
          logSuggestEdit('GENERATOR — trimmed accepted baseline whitespace', {
            ...debugMeta,
            trimmedCount: trimmedBaseline.trimmedCount,
            before: textSnapshot(originalText),
            after: textSnapshot(trimmedBaseline.text),
          });
          originalText = trimmedBaseline.text;
          textMap = trimmedBaseline.map;
        }
      }

      log(`[RobustXmlTrackChangesGenerator] Extracted accepted text length: ${originalText.length}`);
      log(`[RobustXmlTrackChangesGenerator] Accepted text preview: ${originalText.substring(0, 150)}`);
      log(`[RobustXmlTrackChangesGenerator] Created position map with ${textMap.length} entries`);

      logSuggestEdit('GENERATOR — accepted baseline (diff FROM this)', {
        ...debugMeta,
        originalText: textSnapshot(originalText),
        textMapEntries: textMap.length,
      });

      logTextBaselineComparison(
        'newText (engine suggestion)',
        newText,
        'originalText (OOXML accepted + position map)',
        originalText,
      );

      if (textMap.length !== originalText.length) {
        logError(
          '[RobustXmlTrackChangesGenerator] Length mismatch: extracted text length ' +
            originalText.length +
            ' vs position map ' +
            textMap.length,
        );
        const previewStart = Math.max(0, originalText.length - 20);
        logError('[RobustXmlTrackChangesGenerator] Mismatch debug', {
          textTail: originalText.slice(previewStart),
          mapTailCharIndex: textMap[textMap.length - 1]?.charIndex,
          hasFieldRefs,
          workingOoxmlLen: workingOoxml.length,
        });
        logSuggestEditWarn('GENERATOR — map length ≠ text length', {
          ...debugMeta,
          originalTextLen: originalText.length,
          mapLen: textMap.length,
        });
      }

      if (isLiveIncremental && diffBaselineText) {
        if (diffBaselineText === newText) {
          log('[RobustXmlTrackChangesGenerator] No changes detected (live incremental exact match)');
          logSuggestEdit('GENERATOR — no-op (DOM baseline equals new text)', { ...debugMeta });
          return workingOoxml;
        }
      } else if (originalText.trim() === newText.trim()) {
        log('[RobustXmlTrackChangesGenerator] No changes detected, returning sanitized original XML');
        logSuggestEdit('GENERATOR — no-op (trimmed texts equal)', { ...debugMeta });
        return workingOoxml;
      }

      logSuggestEdit('GENERATOR — generation mode', {
        ...debugMeta,
        mode: generationMode,
        simpleEditDiffStrategy:
          generationMode === 'simple-edit' ? simpleEditDiffStrategy : undefined,
      });

      // Selection-replace hint short-circuit: when the caller knows the user typed/pasted
      // over a selection, skip LCS and emit a single delete + single insert. LCS would
      // otherwise look for matching chars across the selection boundary (e.g. typed "ס"
      // aligned with the original "ס" inside the selected "יתווסף"), splitting the
      // deletion and dropping characters from the insertion.
      const editSelectionHint = options?.editSelectionHint;
      const canUseEditSelectionHint =
        isLiveIncremental &&
        editSelectionHint &&
        editSelectionHint.start >= 0 &&
        editSelectionHint.end > editSelectionHint.start &&
        editSelectionHint.end <= originalText.length;

      let diffOps: DiffOperation[];
      if (canUseEditSelectionHint && editSelectionHint) {
        diffOps = this.buildEditSelectionHintOps(
          originalText,
          editSelectionHint.start,
          editSelectionHint.end,
          editSelectionHint.insertedText,
        );
        log(
          `[RobustXmlTrackChangesGenerator] Edit-selection hint: ${diffOps.length} ops (skipping LCS)`,
        );
        logSuggestEdit('DIFF — selection-replace hint', {
          ...debugMeta,
          start: editSelectionHint.start,
          end: editSelectionHint.end,
          deletedLen: editSelectionHint.end - editSelectionHint.start,
          insertedLen: editSelectionHint.insertedText.length,
          deletedSnippet: textSnapshot(
            originalText.slice(editSelectionHint.start, editSelectionHint.end),
          ).preview,
          insertedSnippet: textSnapshot(editSelectionHint.insertedText).preview,
        });
      } else {
        // Step 3: Word-level for document apply; char-level for inline clause-card edits.
        // Live typing uses incremental-char (prev → next LCS) so Word can split counterparty
        // w:ins and add localized user w:ins siblings. Save / full replace uses contiguous.
        diffOps =
          diffGranularity === 'char'
            ? generationMode === 'simple-edit'
              ? simpleEditDiffStrategy === 'incremental-char'
                ? this.computeCharLevelDiff(originalText, newText, debugMeta)
                : this.computeContiguousCharLevelDiff(originalText, newText, debugMeta)
              : this.computeCharLevelDiff(originalText, newText, debugMeta)
            : this.computeHybridDiff(originalText, newText, debugMeta);
      }

      // Whitespace extension is for word-level redline-insert gaps only; char-level
      // simple-edit must keep exact boundaries (e.g. delete "הואיל" but keep the space before it).
      if (generationMode !== 'simple-edit') {
        diffOps = this.extendDeletionsOverPrecedingWhitespace(originalText, diffOps);
      }

      if (generationMode === 'simple-edit') {
        diffOps = this.applySimpleEditDeletionSemantics(diffOps, textMap, originalText, debugMeta);
      }
      log(`[RobustXmlTrackChangesGenerator] Computed ${diffOps.length} diff operations`);
      logDiffOperations(originalText, diffOps);

      // Step 4: Apply diff to sanitized XML structure
      const editedXml = this.applyDiffToXml(
        workingOoxml,
        diffOps,
        textMap,
        authorName,
        generationMode,
        simpleEditDiffStrategy,
      );
      log('[RobustXmlTrackChangesGenerator] Successfully generated edited XML');

      const acceptedFromResult = TrackChangesParser.extractAcceptedText(editedXml);
      logSuggestEdit('GENERATOR — AFTER applyDiffToXml', {
        ...debugMeta,
        editedXmlLen: editedXml.length,
        acceptedFromResult: textSnapshot(acceptedFromResult),
        intendedNewText: textSnapshot(newText),
        acceptedMatchesNew: acceptedFromResult.replace(/\s+/g, ' ').trim() === newText.replace(/\s+/g, ' ').trim(),
      });

      return editedXml;
    } catch (error) {
      logError('[RobustXmlTrackChangesGenerator] Failed to generate track changes XML', error);
      // Fallback: return original XML
      return originalOoxml;
    }
  }

  /**
   * Find paragraph element in XML document
   */
  private static findParagraphElement(xmlDoc: Document): Element | null {
    // Try different methods to find paragraph
    let para = xmlDoc.querySelector('w\\:p');
    if (!para) para = xmlDoc.querySelector('p');
    if (!para) {
      // Use TreeWalker to find element with local name 'p'
      const walker = xmlDoc.createTreeWalker(
        xmlDoc.documentElement,
        NodeFilter.SHOW_ELEMENT,
        {
          acceptNode: (node: Node) => {
            const elem = node as Element;
            return this.isTag(elem, 'p') ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
          }
        }
      );
      para = walker.nextNode() as Element;
    }
    return para;
  }

  /**
   * Build accepted (final) plain text and a per-character XML position map in one traversal.
   * OOXML should already be sanitized (sanitizeFieldReferencesInOoxml) when it contains REF fields.
   */
  private static buildAcceptedTextAndPositionMap(ooxml: string): {
    text: string;
    map: TextPositionMap[];
  } {
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(ooxml, 'text/xml');
      const paragraphElement = this.findParagraphElement(xmlDoc);
      if (!paragraphElement) {
        return { text: '', map: [] };
      }

      const positionMap: TextPositionMap[] = [];
      const textChars: string[] = [];
      this.traverseAndMapPositions(
        paragraphElement,
        positionMap,
        textChars,
        0,
        false,
        null,
        null,
      );
      return { text: textChars.join(''), map: positionMap };
    } catch (error) {
      logError('[RobustXmlTrackChangesGenerator] Error building accepted text + map', error);
      return { text: '', map: [] };
    }
  }

  private static pushMappedChar(
    positionMap: TextPositionMap[],
    textChars: string[],
    ch: string,
    meta: Omit<TextPositionMap, 'charIndex'>,
  ): void {
    const charIndex = textChars.length;
    textChars.push(ch);
    positionMap.push({ charIndex, ...meta });
  }

  private static getElementAuthor(element: Element): string {
    return (
      element.getAttribute('w:author') ||
      element.getAttributeNS(this.W_NAMESPACE, 'author') ||
      element.getAttribute('author') ||
      ''
    );
  }

  /** Blue-side authors whose pending insertions shrink silently on user edit. */
  private static isOurSideAuthor(author: string): boolean {
    return isOurSideTrackChangesAuthor(author);
  }

  /**
   * Traverse XML tree and map character positions
   * @param element - Current element
   * @param positionMap - Position map to populate (mutated)
   * @param charIndex - Current character index (mutated via return value)
   * @param isInsideOriginalIns - Whether we're currently inside an original w:ins element
   * @param originalInsElement - The innermost w:ins element if applicable
   * @param originalInsAuthor - Author on the innermost w:ins element
   * @returns Updated character index
   */
  private static traverseAndMapPositions(
    element: Element,
    positionMap: TextPositionMap[],
    textChars: string[],
    charIndex: number,
    isInsideOriginalIns: boolean,
    originalInsElement: Element | null,
    originalInsAuthor: string | null,
  ): number {
    let currentCharIndex = charIndex;

    // Check if this element is a w:ins - update context
    const localName = this.getLocalName(element);
    let currentIsInsideIns = isInsideOriginalIns;
    let currentOriginalInsElement = originalInsElement;
    let currentInsAuthor = originalInsAuthor;

    if (localName === 'ins') {
      currentIsInsideIns = true;
      currentOriginalInsElement = element;
      currentInsAuthor = this.getElementAuthor(element);
    }

    // Check if this is a w:r (run) element
    const isRun = this.isTag(element, 'r');
    let runElement: Element | null = null;
    let runProperties: Element | null = null;

    if (isRun) {
      runElement = element;
      // Find w:rPr (run properties) if it exists
      for (const child of Array.from(element.childNodes)) {
        if (child.nodeType === Node.ELEMENT_NODE) {
          const childElem = child as Element;
          if (this.isTag(childElem, 'rPr')) {
            runProperties = childElem;
            break;
          }
        }
      }
    }

    // Traverse child nodes
    for (const child of Array.from(element.childNodes)) {
      if (child.nodeType === Node.TEXT_NODE) {
        // Text node - map each character
        const text = child.textContent || '';
        for (let i = 0; i < text.length; i++) {
          this.pushMappedChar(positionMap, textChars, text[i], {
            run: runElement || element,
            textNode: child,
            offset: i,
            runProperties: runProperties,
            isFromOriginalIns: currentIsInsideIns,
            originalInsElement: currentOriginalInsElement,
            originalInsAuthor: currentInsAuthor,
          });
          currentCharIndex++;
        }
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        const childElement = child as Element;
        const childLocalName = this.getLocalName(childElement);

        // Skip w:del elements (we don't map deleted text, but we'll preserve them in output)
        if (childLocalName === 'del') {
          continue;
        }

        // Skip Word field instructions and field scaffolding (REF … MERGEFORMAT)
        if (
          childLocalName === 'instrtext' ||
          childLocalName === 'fldchar' ||
          childLocalName === 'fldsimple'
        ) {
          continue;
        }

        // For w:t (text) elements, extract text and map positions
        if (childLocalName === 't') {
          const text = childElement.textContent || '';
          for (let i = 0; i < text.length; i++) {
            this.pushMappedChar(positionMap, textChars, text[i], {
              run: runElement || element,
              textNode: childElement.firstChild || childElement,
              offset: i,
              runProperties: runProperties,
              isFromOriginalIns: currentIsInsideIns,
              originalInsElement: currentOriginalInsElement,
              originalInsAuthor: currentInsAuthor,
            });
            currentCharIndex++;
          }
          continue;
        }

        // Skip w:delText (deleted text)
        if (childLocalName === 'delText') {
          continue;
        }

        // For w:ins elements, traverse with updated context
        if (childLocalName === 'ins') {
          currentCharIndex = this.traverseAndMapPositions(
            childElement,
            positionMap,
            textChars,
            currentCharIndex,
            true,
            childElement,
            this.getElementAuthor(childElement),
          );
          continue;
        }

        // For w:rPr, skip (it's run properties, not text)
        if (childLocalName === 'rPr') {
          continue;
        }

        // For w:tab, map a tab character (Word renders these as whitespace)
        if (childLocalName === 'tab') {
          this.pushMappedChar(positionMap, textChars, '\t', {
            run: runElement || element,
            textNode: childElement,
            offset: 0,
            runProperties: runProperties,
            isFromOriginalIns: currentIsInsideIns,
            originalInsElement: currentOriginalInsElement,
            originalInsAuthor: currentInsAuthor,
          });
          currentCharIndex++;
          continue;
        }

        if (childLocalName === 'br') {
          this.pushMappedChar(positionMap, textChars, '\n', {
            run: runElement || element,
            textNode: childElement,
            offset: 0,
            runProperties: runProperties,
            isFromOriginalIns: currentIsInsideIns,
            originalInsElement: currentOriginalInsElement,
            originalInsAuthor: currentInsAuthor,
          });
          currentCharIndex++;
          continue;
        }

        currentCharIndex = this.traverseAndMapPositions(
          childElement,
          positionMap,
          textChars,
          currentCharIndex,
          currentIsInsideIns,
          currentOriginalInsElement,
          currentInsAuthor,
        );
      }
    }

    return currentCharIndex;
  }

  /**
   * Trim leading/trailing whitespace from accepted text + map (Word often prefixes paragraph tabs).
   * Keeps textMap[i].charIndex aligned with text[i].
   */
  private static trimLeadingTrailingWhitespaceFromTextMap(
    text: string,
    map: TextPositionMap[],
  ): { text: string; map: TextPositionMap[]; trimmedCount: number } {
    let start = 0;
    while (start < text.length && /\s/.test(text[start])) start++;
    let end = text.length;
    while (end > start && /\s/.test(text[end - 1])) end--;
    if (start === 0 && end === text.length) {
      return { text, map, trimmedCount: 0 };
    }
    const trimmedText = text.slice(start, end);
    const trimmedMap = map.slice(start, end).map((entry, charIndex) => ({
      ...entry,
      charIndex,
    }));
    return { text: trimmedText, map: trimmedMap, trimmedCount: start + (text.length - end) };
  }

  /**
   * Reconcile OOXML position map when DOM baseline matches content but whitespace runs
   * differ (editable HTML preserves spaces; extractAcceptedText / post-processors collapse them).
   */
  private static alignTextMapToDomBaseline(
    domBaseline: string,
    ooxmlText: string,
    ooxmlMap: TextPositionMap[],
  ): { text: string; map: TextPositionMap[] } {
    if (domBaseline === ooxmlText) {
      return { text: domBaseline, map: ooxmlMap };
    }

    const map: TextPositionMap[] = [];
    let oi = 0;

    for (let di = 0; di < domBaseline.length; di++) {
      const dc = domBaseline[di];

      if (oi < ooxmlText.length && dc === ooxmlText[oi]) {
        map.push({ ...ooxmlMap[oi], charIndex: di });
        oi++;
        continue;
      }

      const domIsWs = /\s/.test(dc);
      const ooxmlIsWs = oi < ooxmlText.length && /\s/.test(ooxmlText[oi]);

      if (domIsWs && ooxmlIsWs) {
        map.push({ ...ooxmlMap[oi], charIndex: di });
        oi++;
        continue;
      }

      if (domIsWs && oi > 0) {
        map.push({ ...ooxmlMap[oi - 1], charIndex: di });
        continue;
      }

      if (ooxmlIsWs) {
        oi++;
        di--;
        continue;
      }

      const anchor = ooxmlMap[Math.min(oi, ooxmlMap.length - 1)];
      map.push({ ...anchor, charIndex: di });
      if (oi < ooxmlText.length && dc === ooxmlText[oi]) {
        oi++;
      }
    }

    return { text: domBaseline, map };
  }

  /**
   * Word-level LCS skips whitespace between tokens; extend each deletion backward over
   * whitespace gaps so orphaned spaces (e.g. "בנספח  א'") are not left before insertions.
   */
  /**
   * Reproduce Word's "track changes on, user edits the doc" semantics for deletions.
   *
   * - Our-side pending insertions (AI Suggestion / User Edit): deleting characters
   *   silently shrinks the w:ins — no w:del, as if the text was never committed.
   * - Counterparty insertions and original document text: deletions produce w:del
   *   (strikethrough), matching Word when the user replaces text inside a redline.
   *
   * Deletions that straddle boundaries are split so each segment gets the right treatment.
   */
  private static applySimpleEditDeletionSemantics(
    ops: DiffOperation[],
    textMap: TextPositionMap[],
    baselineText: string,
    debugMeta?: { source?: string; clauseId?: string; clauseLabel?: string | number },
  ): DiffOperation[] {
    type DelClass = 'our-ins' | 'visible';
    const classify = (pos: number): DelClass => {
      if (pos < 0 || pos >= textMap.length) return 'visible';
      const m = textMap[pos];
      if (m.isFromOriginalIns && this.isOurSideAuthor(m.originalInsAuthor ?? '')) {
        return 'our-ins';
      }
      return 'visible';
    };

    const result: DiffOperation[] = [];
    let silenced = 0;
    let kept = 0;
    let split = 0;

    for (const op of ops) {
      if (op.type !== 'deletion') {
        result.push(op);
        continue;
      }
      if (op.endPos <= op.startPos) {
        result.push(op);
        continue;
      }

      let segStart = op.startPos;
      let segClass = classify(op.startPos);
      const segments: Array<{ start: number; end: number; silent: boolean }> = [];

      for (let i = op.startPos + 1; i < op.endPos; i++) {
        const here = classify(i);
        if (here !== segClass) {
          segments.push({ start: segStart, end: i, silent: segClass === 'our-ins' });
          segStart = i;
          segClass = here;
        }
      }
      segments.push({ start: segStart, end: op.endPos, silent: segClass === 'our-ins' });

      if (segments.length > 1) split++;

      for (const seg of segments) {
        const text = baselineText.slice(seg.start, seg.end);
        if (seg.silent) silenced++; else kept++;
        result.push({
          type: 'deletion',
          text,
          startPos: seg.start,
          endPos: seg.end,
          ...(seg.silent ? { silentRemoval: true } : {}),
        });
      }
    }

    logSuggestEdit('GENERATOR — simple-edit deletion semantics', {
      ...debugMeta,
      totalDeletionOps: ops.filter((o) => o.type === 'deletion').length,
      splitDeletions: split,
      silentSegments: silenced,
      visibleSegments: kept,
    });

    return result;
  }

  private static extendDeletionsOverPrecedingWhitespace(
    text: string,
    ops: DiffOperation[],
  ): DiffOperation[] {
    return ops.map((op) => {
      if (op.type !== 'deletion' || op.startPos <= 0) return op;
      let start = op.startPos;
      while (start > 0 && /\s/.test(text[start - 1])) {
        start--;
      }
      if (start === op.startPos) return op;
      return {
        ...op,
        startPos: start,
        text: text.slice(start, op.endPos),
      };
    });
  }

  /** Split text into words with start/end character indices (for hybrid diff). */
  private static splitWordsWithPositions(text: string): Array<{ word: string; startChar: number; endChar: number }> {
    const words: Array<{ word: string; startChar: number; endChar: number }> = [];
    const wordRegex = /\S+/g;
    let match;

    while ((match = wordRegex.exec(text)) !== null) {
      words.push({
        word: match[0],
        startChar: match.index,
        endChar: match.index + match[0].length
      });
    }

    return words;
  }

  /**
   * Compute word-level diff using LCS algorithm
   * @param originalText - Original plain text
   * @param newText - New plain text
   * @returns Array of diff operations with character positions
   */
  /**
   * Compute diff using the SAME algorithm as RedlineInserter (from TextDiff.ts).
   * This ensures the "Suggested edit" panel shows the exact same changes
   * that will be applied to the Word document.
   *
   * Steps:
   * 1. Normalize both texts (collapse whitespace to single spaces) — same as RedlineInserter
   * 2. Use the shared computeWordDiff from TextDiff.ts — same LCS with same tie-breaking
   * 3. Map word-level results back to character positions in the original (un-normalized) text
   */
  private static computeHybridDiff(
    originalText: string,
    newText: string,
    debugMeta?: { source?: string; clauseId?: string; clauseLabel?: string | number },
  ): DiffOperation[] {
    // 1. Normalize texts for diffing (same normalization as RedlineInserter)
    const origNormalized = originalText.replace(/\s+/g, ' ').trim();
    const newNormalized = newText.replace(/\s+/g, ' ').trim();

    logSuggestEdit('DIFF — normalized inputs', {
      ...debugMeta,
      origNormalized: textSnapshot(origNormalized),
      newNormalized: textSnapshot(newNormalized),
    });

    // 2. Use the shared word diff (same algorithm as RedlineInserter)
    const sharedDiffs: DiffOp[] = computeSharedWordDiff(origNormalized, newNormalized);
    log(`[RobustXmlTrackChangesGenerator] Shared word diff: ${sharedDiffs.length} ops`);

    const origWordsNormalized = origNormalized.split(/\s+/).filter(Boolean);
    const newWordsNormalized = newNormalized.split(/\s+/).filter(Boolean);
    logWordList('DIFF — origNormalized words', origWordsNormalized);
    logWordList('DIFF — newNormalized words', newWordsNormalized);

    logSuggestEdit('DIFF — shared word ops (LCS)', {
      ...debugMeta,
      ops: sharedDiffs.map((op, i) => ({
        i,
        type: op.type,
        words: op.words,
        wordCount: op.words.length,
      })),
    });

    // 3. Map LCS word indices to character positions in the same string used for applyDiffToXml
    const origWordPositions = this.splitWordsWithPositions(originalText);
    const origWordsRaw = origWordPositions.map((w) => w.word);

    logWordList('DIFF — originalText raw words (position map)', origWordsRaw);
    logSuggestEdit('DIFF — word index alignment check', {
      ...debugMeta,
      rawWordCount: origWordsRaw.length,
      normalizedWordCount: origWordsNormalized.length,
      countsMatch: origWordsRaw.length === origWordsNormalized.length,
      firstRawWord: origWordsRaw[0],
      firstNormWord: origWordsNormalized[0],
      lastRawWord: origWordsRaw[origWordsRaw.length - 1],
      lastNormWord: origWordsNormalized[origWordsNormalized.length - 1],
    });

    if (origWordsRaw.length !== origWordsNormalized.length) {
      logSuggestEditWarn('DIFF — word count mismatch (raw vs normalized)', {
        ...debugMeta,
        hint: 'Mapping LCS word indices to char positions will skew — likely leading/double spaces in OOXML accepted text.',
        rawWordCount: origWordsRaw.length,
        normalizedWordCount: origWordsNormalized.length,
      });
    }

    // 4. Convert word-level DiffOps to character-position DiffOperations
    const result: DiffOperation[] = [];
    let origWordIdx = 0; // Index into origWordPositions

    for (const diffOp of sharedDiffs) {
      if (diffOp.type === 'equal') {
        // Unchanged words: advance through original word positions
        const startWord = origWordIdx;
        const endWord = origWordIdx + diffOp.words.length;
        if (startWord < origWordPositions.length && endWord <= origWordPositions.length) {
          result.push({
            type: 'unchanged',
            text: diffOp.words.join(' '),
            startPos: origWordPositions[startWord].startChar,
            endPos: origWordPositions[endWord - 1].endChar
          });
        }
        origWordIdx += diffOp.words.length;
      } else if (diffOp.type === 'delete') {
        // Deleted words: map to original positions and advance
        const startWord = origWordIdx;
        const endWord = origWordIdx + diffOp.words.length;
        if (startWord < origWordPositions.length && endWord <= origWordPositions.length) {
          result.push({
            type: 'deletion',
            text: diffOp.words.join(' '),
            startPos: origWordPositions[startWord].startChar,
            endPos: origWordPositions[endWord - 1].endChar
          });
        }
        origWordIdx += diffOp.words.length;
      } else if (diffOp.type === 'insert') {
        // Inserted words: don't advance original position
        // Insert position = end of previous word, or start of text
        const insertPos = origWordIdx > 0
          ? origWordPositions[origWordIdx - 1].endChar
          : 0;
        result.push({
          type: 'insertion',
          text: diffOp.words.join(' '),
          startPos: insertPos,
          endPos: insertPos
        });
      }
    }

    log(`[RobustXmlTrackChangesGenerator] Converted to ${result.length} position-based ops`);

    // Shared punctuation-affix pass — same helper the hybrid engine uses (see punctuationAffix.ts).
    // Shrinks each adjacent [deletion, insertion] pair by the char-level common prefix/suffix when
    // the delete middle has no letter/digit, so both this legacy preview and the hybrid preview
    // produce the same redline (e.g. `המלווה".` → `המלווה"` stays kept, only `.` is struck).
    const affixedResult = this.extractPunctuationAffixesFromOps(result, originalText);

    logSuggestEdit('DIFF — position ops summary', {
      ...debugMeta,
      origWordIdxAfter: origWordIdx,
      origWordPositionsLen: origWordPositions.length,
      origWordIdxExhausted: origWordIdx === origWordPositions.length,
      deletionRanges: affixedResult
        .filter((o) => o.type === 'deletion')
        .map((o) => ({ start: o.startPos, end: o.endPos, text: o.text.slice(0, 60) })),
      insertionRanges: affixedResult
        .filter((o) => o.type === 'insertion')
        .map((o) => ({ at: o.startPos, text: o.text.slice(0, 60) })),
    });

    if (origWordIdx !== origWordPositions.length) {
      logSuggestEditWarn('DIFF — origWordIdx not exhausted', {
        ...debugMeta,
        origWordIdx,
        origWordPositionsLen: origWordPositions.length,
        unconsumedWords: origWordPositions.slice(origWordIdx).map((w) => w.word),
      });
    }

    return affixedResult;
  }

  /**
   * Shrink each adjacent [deletion, insertion] pair by the char-level common prefix/suffix when
   * the delete middle contains no letter/digit — the safe "punctuation-only edge" case
   * (see `word/utils/punctuationAffix.ts`). This keeps a shared word (e.g. `המלווה"`) untouched
   * instead of being struck and re-inserted just because a period vs bracket differs at the edge.
   *
   * Ordering rule when there IS a suffix: emit `insertion` BEFORE the shrunk `deletion`, so the
   * resulting text reads `prefix + insertMiddle + suffix` after apply. When there is no suffix
   * we preserve the legacy order (deletion then insertion at the deleted-word end) so unrelated
   * behavior around insertion padding is untouched.
   */
  private static extractPunctuationAffixesFromOps(
    ops: DiffOperation[],
    originalText: string,
  ): DiffOperation[] {
    const out: DiffOperation[] = [];
    let i = 0;
    while (i < ops.length) {
      const cur = ops[i];
      const next = ops[i + 1];
      if (cur.type === 'deletion' && next && next.type === 'insertion') {
        const delText = originalText.slice(cur.startPos, cur.endPos);
        const affixes = computePunctuationAffixes(delText, next.text);
        if (affixes && (affixes.prefix.length > 0 || affixes.suffix.length > 0)) {
          const newDelStart = cur.startPos + affixes.prefix.length;
          const newDelEnd = cur.endPos - affixes.suffix.length;
          const hasSuffix = affixes.suffix.length > 0;

          if (hasSuffix && affixes.insertMiddle !== '') {
            out.push({
              type: 'insertion',
              text: affixes.insertMiddle,
              startPos: newDelStart,
              endPos: newDelStart,
            });
          }
          if (affixes.deleteMiddle !== '') {
            out.push({
              type: 'deletion',
              text: affixes.deleteMiddle,
              startPos: newDelStart,
              endPos: newDelEnd,
              ...(cur.silentRemoval ? { silentRemoval: true } : {}),
            });
          }
          if (!hasSuffix && affixes.insertMiddle !== '') {
            out.push({
              type: 'insertion',
              text: affixes.insertMiddle,
              startPos: newDelEnd,
              endPos: newDelEnd,
            });
          }
          i += 2;
          continue;
        }
      }
      out.push(cur);
      i += 1;
    }
    return out;
  }

  /**
   * Build deterministic diff ops from an explicit selection-replace hint.
   * Produces: equal(prefix) → delete(selection) → insert(typed) → equal(suffix).
   * Mirrors how Word treats a selection-then-type without running LCS.
   */
  private static buildEditSelectionHintOps(
    originalText: string,
    start: number,
    end: number,
    insertedText: string,
  ): DiffOperation[] {
    const ops: DiffOperation[] = [];
    if (start > 0) {
      ops.push({
        type: 'unchanged',
        text: originalText.slice(0, start),
        startPos: 0,
        endPos: start,
      });
    }
    if (end > start) {
      ops.push({
        type: 'deletion',
        text: originalText.slice(start, end),
        startPos: start,
        endPos: end,
      });
    }
    if (insertedText.length > 0) {
      ops.push({
        type: 'insertion',
        text: insertedText,
        startPos: end,
        endPos: end,
      });
    }
    if (end < originalText.length) {
      ops.push({
        type: 'unchanged',
        text: originalText.slice(end),
        startPos: end,
        endPos: originalText.length,
      });
    }
    return ops;
  }

  /** Contiguous char diff for simple-edit — single middle hunk (Word typing semantics). */
  private static computeContiguousCharLevelDiff(
    originalText: string,
    newText: string,
    debugMeta?: { source?: string; clauseId?: string; clauseLabel?: string | number },
  ): DiffOperation[] {
    const sharedDiffs: DiffOp[] = computeSharedContiguousCharDiff(originalText, newText);
    log(`[RobustXmlTrackChangesGenerator] Contiguous char diff: ${sharedDiffs.length} ops`);
    logSuggestEdit('DIFF — contiguous char ops', {
      ...debugMeta,
      ops: sharedDiffs.map((op, i) => ({
        i,
        type: op.type,
        text: op.words.join(''),
        len: op.words.length,
      })),
    });
    return this.convertSharedCharDiffsToPositionOps(sharedDiffs);
  }

  /** Character-level diff — precise insertions inside words (Hebrew, no spaces). */
  private static computeCharLevelDiff(
    originalText: string,
    newText: string,
    debugMeta?: { source?: string; clauseId?: string; clauseLabel?: string | number },
  ): DiffOperation[] {
    const sharedDiffs: DiffOp[] = computeSharedCharDiff(originalText, newText);
    log(`[RobustXmlTrackChangesGenerator] Char diff: ${sharedDiffs.length} ops`);
    logSuggestEdit('DIFF — char ops', {
      ...debugMeta,
      ops: sharedDiffs.map((op, i) => ({
        i,
        type: op.type,
        text: op.words.join(''),
        len: op.words.length,
      })),
    });

    return this.convertSharedCharDiffsToPositionOps(sharedDiffs);
  }

  private static convertSharedCharDiffsToPositionOps(sharedDiffs: DiffOp[]): DiffOperation[] {
    const result: DiffOperation[] = [];
    let pos = 0;
    for (const diffOp of sharedDiffs) {
      const text = diffOp.words.join('');
      if (diffOp.type === 'equal') {
        result.push({
          type: 'unchanged',
          text,
          startPos: pos,
          endPos: pos + text.length,
        });
        pos += text.length;
      } else if (diffOp.type === 'delete') {
        result.push({
          type: 'deletion',
          text,
          startPos: pos,
          endPos: pos + text.length,
        });
        pos += text.length;
      } else if (diffOp.type === 'insert') {
        result.push({
          type: 'insertion',
          text,
          startPos: pos,
          endPos: pos,
        });
      }
    }
    return result;
  }

  /**
   * Apply diff operations to XML structure, creating new OOXML with track changes
   * @param originalOoxml - Original OOXML
   * @param diffOps - Diff operations
   * @param textMap - Text position mappings
   * @param authorName - Author name for track changes
   * @returns New OOXML with track changes
   */
  private static applyDiffToXml(
    originalOoxml: string,
    diffOps: DiffOperation[],
    textMap: TextPositionMap[],
    authorName: string,
    generationMode: TrackChangesGenerationMode = 'redline-insert',
    simpleEditDiffStrategy: SimpleEditDiffStrategy = 'contiguous',
  ): string {
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(originalOoxml, 'text/xml');
      
      // Find paragraph element
      const paragraphElement = this.findParagraphElement(xmlDoc);
      if (!paragraphElement) {
        logError('[RobustXmlTrackChangesGenerator] No paragraph element found for applying diff');
        return originalOoxml;
      }

      // Build new paragraph with track changes
      const newParagraph = this.buildParagraphWithTrackChanges(
        paragraphElement,
        diffOps,
        textMap,
        authorName,
        xmlDoc,
        generationMode,
      );

      // Flatten any <w:ins> nested directly inside another <w:ins>.
      // Word's insertOoxml rejects nested w:ins, so we split the outer w:ins
      // around the inner one: [outer-before][inner][outer-after] as siblings.
      // This keeps all authorship correct while producing valid OOXML.
      const serializedBeforeFlatten = new XMLSerializer().serializeToString(newParagraph);
      const flattenResult = this.flattenNestedWIns(newParagraph);
      const serializedAfterFlatten = new XMLSerializer().serializeToString(newParagraph);
      log('[RobustXmlTrackChangesGenerator] flattenNestedWIns applied', {
        splitsPerformed: flattenResult.splitsPerformed,
        nestedInsBefore: flattenResult.nestedInsBefore,
        nestedInsAfter: flattenResult.nestedInsAfter,
        splitDetails: flattenResult.splitDetails,
        xmlLenBefore: serializedBeforeFlatten.length,
        xmlLenAfter: serializedAfterFlatten.length,
        xmlLenDelta: serializedAfterFlatten.length - serializedBeforeFlatten.length,
      });

      // Post-processors relocate / coalesce spaces between w:ins blocks; for live typing
      // only run the lossless merge of adjacent same-author user w:ins so insertOoxml
      // gets a single block per word instead of one block per keystroke.
      if (generationMode === 'simple-edit') {
        if (simpleEditDiffStrategy === 'incremental-char') {
          const merged = this.mergeAdjacentSiblingWIns(newParagraph);
          if (merged > 0) {
            log('[RobustXmlTrackChangesGenerator] mergeAdjacentSiblingWIns (live) applied', {
              merged,
            });
          }
        } else {
          this.postProcessWordLikeInsSegments(newParagraph);
        }
      }

      // Replace old paragraph with new one
      if (paragraphElement.parentNode) {
        paragraphElement.parentNode.replaceChild(newParagraph, paragraphElement);
      } else {
        // If paragraph is root, return just the new paragraph
        return this.ensureXmlnsW(new XMLSerializer().serializeToString(newParagraph));
      }

      return this.ensureXmlnsW(new XMLSerializer().serializeToString(xmlDoc));
    } catch (error) {
      logError('[RobustXmlTrackChangesGenerator] Error applying diff to XML', error);
      return originalOoxml;
    }
  }

  /**
   * WebKit/Word Mac XMLSerializer often emits createElementNS nodes with a default
   * xmlns and cloned children still prefixed `w:`. Without xmlns:w the fragment is
   * invalid XML and the suggested-edit preview falls back to mashed plain text.
   */
  private static ensureXmlnsW(xml: string): string {
    if (!xml || /xmlns:w=/i.test(xml)) return xml;
    const stripped = xml.replace(/^<\?xml[^?]*\?>\s*/i, '');
    const decl = xml.slice(0, xml.length - stripped.length);
    return decl + stripped.replace(/^(<[^\s/>]+)/, `$1 xmlns:w="${this.W_NAMESPACE}"`);
  }

  private static isCounterpartySplitActive(context: TraverseContext): boolean {
    return (
      context.generationMode === 'simple-edit' && context.counterpartySplitSource !== null
    );
  }

  private static closeCounterpartyInsSegment(context: TraverseContext): void {
    context.openCounterpartyIns = null;
  }

  private static ensureCounterpartyInsSegment(context: TraverseContext): Element {
    if (!context.openCounterpartyIns && context.counterpartySplitSource) {
      const ins = context.counterpartySplitSource.cloneNode(false) as Element;
      context.paragraphRoot.appendChild(ins);
      context.openCounterpartyIns = ins;
    }
    return context.openCounterpartyIns ?? context.paragraphRoot;
  }

  /**
   * Word simple-edit: counterparty w:ins is split into sibling blocks at paragraph level;
   * user w:del / w:ins are siblings between them. Our-side w:ins keeps nested edits.
   */
  private static resolveEmitParent(
    context: TraverseContext,
    op: 'unchanged' | 'insertion' | 'deletion',
    silentRemoval: boolean,
    newParent: Element,
  ): Element {
    if (this.isCounterpartySplitActive(context)) {
      if (op === 'unchanged' || (op === 'deletion' && silentRemoval)) {
        return this.ensureCounterpartyInsSegment(context);
      }
      this.closeCounterpartyInsSegment(context);
      return context.paragraphRoot;
    }

    if (op === 'unchanged') {
      if (context.generationMode === 'simple-edit' && context.parentInsElement) {
        return context.parentInsElement;
      }
      return context.parentInsElement || newParent;
    }

    return context.parentInsElement || context.paragraphRoot;
  }

  private static mergeAdjacentSiblingWIns(para: Element): number {
    let mergeCount = 0;
    let i = 0;
    while (i < para.childNodes.length - 1) {
      const a = para.childNodes[i];
      const b = para.childNodes[i + 1];
      if (a.nodeType !== Node.ELEMENT_NODE || b.nodeType !== Node.ELEMENT_NODE) {
        i++;
        continue;
      }
      const aEl = a as Element;
      const bEl = b as Element;
      if (this.getLocalName(aEl) !== 'ins' || this.getLocalName(bEl) !== 'ins') {
        i++;
        continue;
      }
      if (this.getElementAuthor(aEl) !== this.getElementAuthor(bEl)) {
        i++;
        continue;
      }
      // Word keeps counterparty punctuation (e.g. comma) in separate w:ins siblings.
      if (!this.isOurSideAuthor(this.getElementAuthor(aEl))) {
        i++;
        continue;
      }
      while (bEl.firstChild) {
        aEl.appendChild(bEl.firstChild);
      }
      para.removeChild(bEl);
      mergeCount++;
    }
    return mergeCount;
  }

  private static extractTextFromIns(ins: Element): string {
    let text = '';
    for (const child of Array.from(ins.childNodes)) {
      if (child.nodeType === Node.ELEMENT_NODE && this.isTag(child as Element, 'r')) {
        text += this.extractTextFromRun(child as Element);
      }
    }
    return text;
  }

  private static setInsTextContent(ins: Element, text: string, xmlDoc: Document): void {
    let firstRun: Element | null = null;
    const extraRuns: Element[] = [];
    for (const child of Array.from(ins.childNodes)) {
      if (child.nodeType !== Node.ELEMENT_NODE) continue;
      const el = child as Element;
      if (!this.isTag(el, 'r')) continue;
      if (!firstRun) firstRun = el;
      else extraRuns.push(el);
    }
    if (!firstRun) {
      ins.appendChild(this.createBasicRun(xmlDoc, text));
      return;
    }
    for (const run of extraRuns) ins.removeChild(run);
    const textElem = this.getOrCreateTextElement(firstRun, xmlDoc);
    textElem.textContent = text;
    if (text.length > 0 && /^\s|\s$/.test(text)) {
      textElem.setAttribute('xml:space', 'preserve');
    }
  }

  /**
   * Match Word's w:ins text boundaries: leading space on user blocks after comma,
   * leading space on counterparty tails after user blocks (not trailing on user).
   */
  private static normalizeWordLikeInsBoundaries(para: Element): number {
    let adjustments = 0;
    const insChildren: Element[] = [];
    for (const child of Array.from(para.childNodes)) {
      if (child.nodeType === Node.ELEMENT_NODE && this.isTag(child as Element, 'ins')) {
        insChildren.push(child as Element);
      }
    }

    for (let i = 0; i < insChildren.length - 1; i++) {
      const a = insChildren[i];
      const b = insChildren[i + 1];
      const aAuthor = this.getElementAuthor(a);
      const bAuthor = this.getElementAuthor(b);
      let aText = this.extractTextFromIns(a);
      let bText = this.extractTextFromIns(b);
      let changed = false;

      if (!this.isOurSideAuthor(aAuthor) && this.isOurSideAuthor(bAuthor)) {
        if (aText.endsWith(',') && bText.length > 0 && !bText.startsWith(' ')) {
          bText = ` ${bText}`;
          changed = true;
        }
      }

      // Only relocate trailing space on the first user w:ins (Word's push-aside after מצהירה,).
      // Later user blocks (e.g. " איזה יום") keep internal spaces.
      if (
        this.isOurSideAuthor(aAuthor) &&
        !this.isOurSideAuthor(bAuthor) &&
        this.isFirstOurSideInsSibling(para, a)
      ) {
        if (aText.endsWith(' ') && !aText.endsWith('  ') && bText.length > 0 && !bText.startsWith(' ')) {
          aText = aText.slice(0, -1);
          bText = ` ${bText}`;
          changed = true;
        }
      }

      if (changed) {
        const xmlDoc = para.ownerDocument;
        if (xmlDoc) {
          this.setInsTextContent(a, aText, xmlDoc);
          this.setInsTextContent(b, bText, xmlDoc);
          adjustments++;
        }
      }
    }

    return adjustments;
  }

  private static isFirstOurSideInsSibling(para: Element, ins: Element): boolean {
    for (const child of Array.from(para.childNodes)) {
      if (child.nodeType !== Node.ELEMENT_NODE || !this.isTag(child as Element, 'ins')) {
        continue;
      }
      const author = this.getElementAuthor(child as Element);
      if (!this.isOurSideAuthor(author)) {
        continue;
      }
      return child === ins;
    }
    return false;
  }

  private static getParagraphInsChildren(para: Element): Element[] {
    const children: Element[] = [];
    for (const child of Array.from(para.childNodes)) {
      if (child.nodeType === Node.ELEMENT_NODE && this.isTag(child as Element, 'ins')) {
        children.push(child as Element);
      }
    }
    return children;
  }

  /**
   * Word merges continued user typing across a whitespace-only counterparty gap
   * (e.g. "איזה" + Dotan " " + "יום" → " איזה יום").
   */
  private static coalesceUserInsAcrossWhitespaceGaps(para: Element): number {
    let coalesced = 0;
    while (true) {
      const insChildren = this.getParagraphInsChildren(para);
      let found = false;
      for (let i = 0; i < insChildren.length - 2; i++) {
        const a = insChildren[i];
        const mid = insChildren[i + 1];
        const b = insChildren[i + 2];
        const aAuthor = this.getElementAuthor(a);
        const bAuthor = this.getElementAuthor(b);
        if (!this.isOurSideAuthor(aAuthor) || this.isOurSideAuthor(this.getElementAuthor(mid))) {
          continue;
        }
        if (!this.isOurSideAuthor(bAuthor) || aAuthor !== bAuthor) {
          continue;
        }

        const midText = this.extractTextFromIns(mid);
        if (!/^\s+$/.test(midText)) {
          continue;
        }

        let combined = this.extractTextFromIns(a) + midText + this.extractTextFromIns(b);
        if (!combined.startsWith(' ')) {
          const prev = i > 0 ? insChildren[i - 1] : null;
          if (!prev || !this.isOurSideAuthor(this.getElementAuthor(prev))) {
            combined = ` ${combined}`;
          }
        }

        const xmlDoc = para.ownerDocument;
        if (!xmlDoc) {
          continue;
        }

        this.setInsTextContent(a, combined, xmlDoc);
        para.removeChild(mid);
        para.removeChild(b);
        coalesced++;
        found = true;
        break;
      }
      if (!found) {
        break;
      }
    }
    return coalesced;
  }

  /** Second and later user w:ins blocks get a leading space after counterparty text. */
  private static ensureLeadingSpaceOnSubsequentUserIns(para: Element): number {
    let adjustments = 0;
    let seenOurSideIns = false;
    for (const ins of this.getParagraphInsChildren(para)) {
      if (!this.isOurSideAuthor(this.getElementAuthor(ins))) {
        continue;
      }
      if (!seenOurSideIns) {
        seenOurSideIns = true;
        continue;
      }
      const text = this.extractTextFromIns(ins);
      if (text.length > 0 && !text.startsWith(' ')) {
        const xmlDoc = para.ownerDocument;
        if (xmlDoc) {
          this.setInsTextContent(ins, ` ${text}`, xmlDoc);
          adjustments++;
        }
      }
    }
    return adjustments;
  }

  private static postProcessWordLikeInsSegments(para: Element): void {
    const merged = this.mergeAdjacentSiblingWIns(para);
    if (merged > 0) {
      log('[RobustXmlTrackChangesGenerator] mergeAdjacentSiblingWIns applied', { merged });
    }

    const normalized = this.normalizeWordLikeInsBoundaries(para);
    if (normalized > 0) {
      log('[RobustXmlTrackChangesGenerator] normalizeWordLikeInsBoundaries applied', {
        adjustments: normalized,
      });
    }

    const leadingSpace = this.ensureLeadingSpaceOnSubsequentUserIns(para);
    if (leadingSpace > 0) {
      log('[RobustXmlTrackChangesGenerator] ensureLeadingSpaceOnSubsequentUserIns applied', {
        adjustments: leadingSpace,
      });
    }

    const coalesced = this.coalesceUserInsAcrossWhitespaceGaps(para);
    if (coalesced > 0) {
      log('[RobustXmlTrackChangesGenerator] coalesceUserInsAcrossWhitespaceGaps applied', {
        coalesced,
      });
    }

    const mergedAgain = this.mergeAdjacentSiblingWIns(para);
    if (mergedAgain > 0) {
      log('[RobustXmlTrackChangesGenerator] mergeAdjacentSiblingWIns (post-coalesce) applied', {
        merged: mergedAgain,
      });
    }
  }

  /**
   * Traverse original paragraph structure and build new paragraph with track changes
   * This method preserves w:del elements in their original positions by traversing
   * the structure element-by-element rather than rebuilding from diff operations.
   */
  private static traverseAndBuildParagraph(
    originalElement: Element,
    newParent: Element,
    diffOps: DiffOperation[],
    textMap: TextPositionMap[],
    xmlDoc: Document,
    context: TraverseContext,
  ): number {
    // Traverse children in order
    for (const child of Array.from(originalElement.childNodes)) {
      if (child.nodeType !== Node.ELEMENT_NODE) continue;
      
      const elem = child as Element;
      const localName = elem.localName;
      
      // Skip paragraph properties (w:pPr) - they're already copied in buildParagraphWithTrackChanges
      if (localName === 'pPr') {
        continue;
      }
      
      // Handle w:del - preserve completely in original position
      if (localName === 'del') {
        log(`[RobustXmlTrackChangesGenerator] Preserving original w:del at text pos ${context.currentTextPos}`);
        const clonedDel = elem.cloneNode(true) as Element;

        const emitDelAtParagraph =
          context.generationMode === 'simple-edit' && context.counterpartySplitSource !== null;
        if (emitDelAtParagraph) {
          this.closeCounterpartyInsSegment(context);
        }
        const targetParent = emitDelAtParagraph
          ? context.paragraphRoot
          : context.parentInsElement || newParent;
        targetParent.appendChild(clonedDel);

        // Do NOT advance text position - deleted text is not in extracted text
        continue;
      }

      // Handle w:ins - preserve structure, traverse children, allow nesting
      if (localName === 'ins') {
        log(`[RobustXmlTrackChangesGenerator] Processing original w:ins at text pos ${context.currentTextPos}`);

        const insAuthor = this.getElementAuthor(elem);
        const useCounterpartySplit =
          context.generationMode === 'simple-edit' && !this.isOurSideAuthor(insAuthor);

        if (useCounterpartySplit) {
          const prevSplit = context.counterpartySplitSource;
          context.counterpartySplitSource = elem;
          context.changeId = this.traverseAndBuildParagraph(
            elem, newParent, diffOps, textMap, xmlDoc, context,
          );
          this.closeCounterpartyInsSegment(context);
          context.counterpartySplitSource = prevSplit;
          continue;
        }

        // Clone w:ins element (attributes only) — our-side ins or redline-insert mode
        let clonedIns = context.processedInsElements.get(elem);
        if (!clonedIns) {
          clonedIns = elem.cloneNode(false) as Element;
          context.processedInsElements.set(elem, clonedIns);

          const targetParent = context.parentInsElement || newParent;
          targetParent.appendChild(clonedIns);
        }

        const oldParentIns = context.parentInsElement;
        context.parentInsElement = clonedIns;
        context.changeId = this.traverseAndBuildParagraph(
          elem, clonedIns, diffOps, textMap, xmlDoc, context,
        );
        context.parentInsElement = oldParentIns;

        continue;
      }
      
      // Handle w:r (text runs) - apply diff operations
      if (localName === 'r') {
        const runText = this.extractTextFromRun(elem);
        const runTextLength = runText.length;
        
        if (runTextLength > 0) {
          log(`[RobustXmlTrackChangesGenerator] Processing run with ${runTextLength} chars at pos ${context.currentTextPos}`);
          
          // Apply diff operations to this text segment
          context.changeId = this.applyDiffToTextSegment(
            elem,
            context.currentTextPos,
            runTextLength,
            diffOps,
            textMap,
            newParent,
            xmlDoc,
            context
          );
          
          // Advance text position
          context.currentTextPos += runTextLength;
        } else {
          // Empty run - copy as-is
          const targetParent = context.parentInsElement || newParent;
          targetParent.appendChild(elem.cloneNode(true));
        }
        
        continue;
      }
      
      // Handle other elements (w:pPr, w:bookmarkStart, etc.) - copy as-is
      log(`[RobustXmlTrackChangesGenerator] Copying element ${localName} as-is`);
      const cloned = elem.cloneNode(true) as Element;
      const targetParent = context.parentInsElement || newParent;
      targetParent.appendChild(cloned);
    }
    
    return context.changeId;
  }
  
  /**
   * Extract text content from a run element (w:r)
   */
  private static extractTextFromRun(run: Element): string {
    let text = '';
    
    // Traverse all child nodes of the run
    for (const child of Array.from(run.childNodes)) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const elem = child as Element;
        if (elem.localName === 't') {
          // Found a text element - add its content
          text += elem.textContent || '';
        } else if (elem.localName === 'tab') {
          // Tab element — represent as tab character
          text += '\t';
        } else if (elem.localName === 'br') {
          // Break element — represent as newline
          text += '\n';
        } else {
          // Recursively check nested elements (shouldn't happen in normal runs, but be safe)
          const nestedText = this.extractTextFromRun(elem);
          text += nestedText;
        }
      } else if (child.nodeType === Node.TEXT_NODE) {
        // Direct text node (unlikely in OOXML, but handle it)
        text += child.textContent || '';
      }
    }
    
    return text;
  }
  
  /**
   * Apply diff operations to a text segment (from a run)
   * Returns updated changeId
   */
  private static applyDiffToTextSegment(
    originalRun: Element,
    segmentStartPos: number,
    segmentLength: number,
    diffOps: DiffOperation[],
    textMap: TextPositionMap[],
    newParent: Element,
    xmlDoc: Document,
    context: TraverseContext,
  ): number {
    const segmentEndPos = segmentStartPos + segmentLength;
    
    // Find diff operations that overlap with this text segment
    const relevantOps = diffOps.filter(op => {
      if (op.type === 'insertion') {
        return op.startPos >= segmentStartPos && op.startPos < segmentEndPos;
      } else {
        return (op.startPos < segmentEndPos && op.endPos > segmentStartPos);
      }
    });
    
    if (relevantOps.length === 0) {
      // No changes in this segment - copy run as-is
      log(`[RobustXmlTrackChangesGenerator] No changes for segment ${segmentStartPos}-${segmentEndPos}, copying run`);
      const targetParent = this.resolveEmitParent(context, 'unchanged', false, newParent);
      targetParent.appendChild(originalRun.cloneNode(true));
      return context.changeId;
    }
    
    log(`[RobustXmlTrackChangesGenerator] Found ${relevantOps.length} ops for segment ${segmentStartPos}-${segmentEndPos}`);
    
    // Process the segment character by character, applying diff operations
    let charPos = segmentStartPos;
    
    while (charPos < segmentEndPos) {
      // Find the operation at this position
      let currentOp: DiffOperation | null = null;
      
      // Check for insertions at this position first
      const insertionOp = relevantOps.find(
        op => op.type === 'insertion' && op.startPos === charPos
      );
      
      if (insertionOp) {
        currentOp = insertionOp;
      } else {
        // Check for deletion or unchanged
        currentOp = relevantOps.find(
          op => op.type !== 'insertion' && charPos >= op.startPos && charPos < op.endPos
        ) || null;
      }
      
      if (!currentOp) {
        const char = charPos < textMap.length ? textMap[charPos] : null;
        if (char) {
          const targetParent = this.resolveEmitParent(context, 'unchanged', false, newParent);
          const lastChild = targetParent.lastChild;
          const isLastChildRun = lastChild && lastChild.nodeType === Node.ELEMENT_NODE && 
                                 this.isTag(lastChild as Element, 'r');
          
          if (!isLastChildRun || (charPos > segmentStartPos && textMap[charPos - 1]?.run !== char.run)) {
            const newRun = this.preserveFormatting(char.run, '');
            targetParent.appendChild(newRun);
          }
          
          const lastRun = targetParent.lastChild as Element;
          const textElem = this.getOrCreateTextElement(lastRun, xmlDoc);
          textElem.textContent = (textElem.textContent || '') + this.getCharFromMap(char);
        }
        charPos++;
        continue;
      }
      
      if (currentOp.type === 'insertion') {
        log(`[RobustXmlTrackChangesGenerator] Creating insertion at pos ${charPos}`);

        const insTarget = this.resolveEmitParent(context, 'insertion', false, newParent);
        const parentInsAuthor =
          context.parentInsElement?.getAttribute('w:author') ||
          context.parentInsElement?.getAttributeNS(
            'http://schemas.openxmlformats.org/wordprocessingml/2006/main',
            'author',
          ) ||
          '';
        logSuggestEdit('[TrackChangeAuthor] createInsertion placement', {
          charPos,
          newAuthor: context.authorName,
          appendTo: this.isCounterpartySplitActive(context)
            ? 'paragraph_level_counterparty_split'
            : context.parentInsElement
              ? 'nested_inside_parent_w_ins'
              : 'paragraph_level',
          parentInsAuthor: parentInsAuthor || null,
          insertTextLen: currentOp.text.length,
          insertSnippet: currentOp.text.slice(0, 80),
        });
        context.changeId = this.createInsertion(
          currentOp,
          textMap,
          insTarget,
          xmlDoc,
          context.authorName,
          context.date,
          context.changeId,
          context.generationMode,
        );
        
        // Don't advance charPos - insertions don't consume original text
        // Remove this operation from relevantOps
        const opIndex = relevantOps.indexOf(currentOp);
        if (opIndex > -1) relevantOps.splice(opIndex, 1);
        continue;
      }
      
      if (currentOp.type === 'deletion') {
        // Process the deleted portion within this segment
        const delStart = Math.max(currentOp.startPos, segmentStartPos);
        const delEnd = Math.min(currentOp.endPos, segmentEndPos);

        if (currentOp.silentRemoval) {
          charPos = delEnd;
          continue;
        }
        
        log(`[RobustXmlTrackChangesGenerator] Creating deletion for pos ${delStart}-${delEnd}`);
        
        // Create a deletion operation for this segment
        const segmentDelOp: DiffOperation = {
          type: 'deletion',
          text: currentOp.text.substring(delStart - currentOp.startPos, delEnd - currentOp.startPos),
          startPos: delStart,
          endPos: delEnd
        };
        
        const targetParent = this.resolveEmitParent(
          context,
          'deletion',
          false,
          newParent,
        );
        context.changeId = this.createDeletion(
          segmentDelOp, textMap, targetParent, xmlDoc, context.authorName, context.date, context.changeId
        );
        
        // Advance to end of deleted portion
        charPos = delEnd;
        continue;
      }
      
      if (currentOp.type === 'unchanged') {
        const unchangedStart = Math.max(currentOp.startPos, segmentStartPos);
        const unchangedEnd = Math.min(currentOp.endPos, segmentEndPos);

        for (let i = unchangedStart; i < unchangedEnd; i++) {
          const char = i < textMap.length ? textMap[i] : null;
          if (char) {
            const targetParent = this.resolveEmitParent(context, 'unchanged', false, newParent);
            const lastChild = targetParent.lastChild;
            const isLastChildRun = lastChild && lastChild.nodeType === Node.ELEMENT_NODE && 
                                   this.isTag(lastChild as Element, 'r');
            
            if (!isLastChildRun || (i > segmentStartPos && textMap[i - 1]?.run !== char.run)) {
              const newRun = this.preserveFormatting(char.run, '');
              targetParent.appendChild(newRun);
            }
            
            const lastRun = targetParent.lastChild as Element;
            const textElem = this.getOrCreateTextElement(lastRun, xmlDoc);
            textElem.textContent = (textElem.textContent || '') + this.getCharFromMap(char);
          }
        }
        
        charPos = unchangedEnd;
        continue;
      }
    }

    return context.changeId;
  }

  /**
   * Build new paragraph element with track changes
   * Preserves original w:ins and w:del elements using structural traversal
   */
  private static buildParagraphWithTrackChanges(
    originalParagraph: Element,
    diffOps: DiffOperation[],
    textMap: TextPositionMap[],
    authorName: string,
    xmlDoc: Document,
    generationMode: TrackChangesGenerationMode,
  ): Element {
    log('[RobustXmlTrackChangesGenerator] Building paragraph with structural traversal');
    log(`[RobustXmlTrackChangesGenerator] Original paragraph has ${originalParagraph.childNodes.length} children`);
    log(`[RobustXmlTrackChangesGenerator] Diff operations: ${diffOps.length}`);
    log(`[RobustXmlTrackChangesGenerator] Text map entries: ${textMap.length}`);
    
    // Create new paragraph element with same attributes
    const newPara = xmlDoc.createElementNS(this.W_NAMESPACE, 'w:p');
    
    // Copy attributes from original paragraph, but skip namespace declarations
    // (xmlns / xmlns:prefix). Copying them causes accumulation across
    // parse/serialize round-trips: when the input paragraph uses a prefix like
    // `ns1` and its xmlns:ns1 attribute gets carried onto our freshly-created
    // `w:p` element, Word (on Windows) re-serializes the inserted OOXML with
    // that prefix and, on the next edit, hands us back a paragraph that
    // contains xmlns:ns1 declared twice — invalid XML that trips the parser
    // and forces the fallback plain-text path (losing track-change markup).
    //
    // We still declare xmlns:w ourselves. WebKit (Word Mac) serializes
    // createElementNS nodes with a default xmlns while cloned children keep the
    // `w:` prefix — without xmlns:w the suggested-edit preview cannot parse
    // w:ins/w:del and drops our-side/their-side redlines.
    try {
      newPara.setAttributeNS('http://www.w3.org/2000/xmlns/', 'xmlns:w', this.W_NAMESPACE);
    } catch {
      newPara.setAttribute('xmlns:w', this.W_NAMESPACE);
    }
    for (let i = 0; i < originalParagraph.attributes.length; i++) {
      const attr = originalParagraph.attributes[i];
      if (attr.name === 'xmlns' || attr.name.startsWith('xmlns:')) {
        continue;
      }
      newPara.setAttribute(attr.name, attr.value);
    }

    // Copy paragraph properties (w:pPr) if they exist - do this before traversal
    // so they appear first in the new paragraph
    for (const child of Array.from(originalParagraph.childNodes)) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const elem = child as Element;
        if (this.isTag(elem, 'pPr')) {
          newPara.appendChild(elem.cloneNode(true));
          break;
        }
      }
    }

    // Initialize context for traversal
    const date = new Date().toISOString();
    const context: TraverseContext = {
      currentTextPos: 0,
      changeId: Math.floor(Math.random() * 1000000),
      authorName,
      date,
      processedInsElements: new Map<Element, Element>(),
      parentInsElement: null,
      generationMode,
      paragraphRoot: newPara,
      counterpartySplitSource: null,
      openCounterpartyIns: null,
    };

    // Traverse the original paragraph structure
    this.traverseAndBuildParagraph(
      originalParagraph,
      newPara,
      diffOps,
      textMap,
      xmlDoc,
      context
    );

    // Emit any insertions at or beyond the end of the text
    // (startPos === textMap.length, i.e. inserted after the last character)
    const endInsertions = diffOps.filter(
      op => op.type === 'insertion' && op.startPos >= context.currentTextPos
    );
    for (const ins of endInsertions) {
      this.closeCounterpartyInsSegment(context);
      context.changeId = this.createInsertion(
        ins,
        textMap,
        newPara,
        xmlDoc,
        context.authorName,
        context.date,
        context.changeId,
        context.generationMode,
      );
    }

    log(`[RobustXmlTrackChangesGenerator] Finished building paragraph:`, {
      finalTextPos: context.currentTextPos,
      finalChangeId: context.changeId,
      newParaChildren: newPara.childNodes.length,
      processedInsElements: context.processedInsElements.size
    });
    
    // Serialize to verify we have content
    const serializer = new XMLSerializer();
    const serialized = serializer.serializeToString(newPara);
    log(`[RobustXmlTrackChangesGenerator] Serialized new paragraph length: ${serialized.length}`);
    log(`[RobustXmlTrackChangesGenerator] Serialized preview: ${serialized.substring(0, 300)}`);
    
    return newPara;
  }


  /**
   * Flatten <w:ins> elements nested directly inside another <w:ins>.
   *
   * Word's Range.insertOoxml rejects a paragraph that contains a <w:ins> as a
   * direct child of another <w:ins>. This happens when the AI suggestion adds an
   * insertion inside a counterparty's existing insertion (e.g. HERZOG added text,
   * and the AI wants to refine part of it).
   *
   * Transform:
   *   <w:ins author="HERZOG">
   *     <w:r>before</w:r>
   *     <w:ins author="AI">new text</w:ins>   ← nested, rejected
   *     <w:r>after</w:r>
   *   </w:ins>
   *
   * Into:
   *   <w:ins author="HERZOG"><w:r>before</w:r></w:ins>
   *   <w:ins author="AI">new text</w:ins>
   *   <w:ins author="HERZOG"><w:r>after</w:r></w:ins>
   *
   * Outer w:ins elements with no children after splitting are dropped.
   * Multiple passes until no more nesting remains.
   */
  private static countNestedIns(para: Element): number {
    const isIns = (el: Element) =>
      (el.localName || el.tagName.split(':').pop()) === 'ins';
    let count = 0;
    const inspect = (el: Element, insideIns: boolean) => {
      const isInsEl = isIns(el);
      if (isInsEl && insideIns) count++;
      const childInside = insideIns || isInsEl;
      for (const child of Array.from(el.childNodes)) {
        if (child.nodeType === Node.ELEMENT_NODE) inspect(child as Element, childInside);
      }
    };
    inspect(para, false);
    return count;
  }

  private static flattenNestedWIns(para: Element): {
    splitsPerformed: number;
    nestedInsBefore: number;
    nestedInsAfter: number;
    splitDetails: Array<{
      outerAuthor: string;
      innerAuthor: string;
      hadBefore: boolean;
      hadAfter: boolean;
    }>;
  } {
    const isIns = (el: Element) =>
      (el.localName || el.tagName.split(':').pop()) === 'ins';
    const W_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
    const getAuthor = (el: Element) =>
      el.getAttribute('w:author') || el.getAttributeNS(W_NS, 'author') || '';

    const nestedInsBefore = this.countNestedIns(para);
    const splitDetails: Array<{
      outerAuthor: string;
      innerAuthor: string;
      hadBefore: boolean;
      hadAfter: boolean;
    }> = [];
    let splitsPerformed = 0;

    let changed = true;
    while (changed) {
      changed = false;
      // Collect all w:ins elements at any depth (snapshot — we'll mutate during iteration).
      const allIns: Element[] = [];
      const collect = (el: Element) => {
        if (isIns(el)) allIns.push(el);
        for (const child of Array.from(el.childNodes)) {
          if (child.nodeType === Node.ELEMENT_NODE) collect(child as Element);
        }
      };
      collect(para);

      for (const outer of allIns) {
        // Find child w:ins elements (direct children only).
        const innerIns = Array.from(outer.childNodes).filter(
          (c): c is Element =>
            c.nodeType === Node.ELEMENT_NODE && isIns(c as Element),
        );
        if (innerIns.length === 0) continue;

        // Process one inner at a time; restart after each structural mutation.
        const inner = innerIns[0];
        const parent = outer.parentNode;
        if (!parent) continue;

        // Collect sibling nodes of `inner` within `outer`: before / after.
        const children = Array.from(outer.childNodes);
        const innerIdx = children.indexOf(inner);
        const nodesBefore = children.slice(0, innerIdx);
        const nodesAfter = children.slice(innerIdx + 1);

        splitDetails.push({
          outerAuthor: getAuthor(outer),
          innerAuthor: getAuthor(inner),
          hadBefore: nodesBefore.length > 0,
          hadAfter: nodesAfter.length > 0,
        });
        log('[RobustXmlTrackChangesGenerator] flattenNestedWIns: splitting', {
          splitIndex: splitsPerformed,
          outerAuthor: getAuthor(outer),
          innerAuthor: getAuthor(inner),
          nodesBeforeCount: nodesBefore.length,
          nodesAfterCount: nodesAfter.length,
        });

        // Clone outer w:ins for the "before" and "after" parts.
        const cloneOuter = (nodes: ChildNode[]): Element | null => {
          if (nodes.length === 0) return null;
          const clone = outer.cloneNode(false) as Element;
          for (const n of nodes) clone.appendChild(n.cloneNode(true));
          return clone;
        };

        const beforeEl = cloneOuter(nodesBefore);
        const afterEl = cloneOuter(nodesAfter);

        // Replace outer with [before?, inner, after?] in parent.
        const fragment = outer.ownerDocument!.createDocumentFragment();
        if (beforeEl) fragment.appendChild(beforeEl);
        fragment.appendChild(inner.cloneNode(true));
        if (afterEl) fragment.appendChild(afterEl);
        parent.replaceChild(fragment, outer);

        splitsPerformed++;
        changed = true;
        break; // restart scan with updated DOM
      }
    }

    const nestedInsAfter = this.countNestedIns(para);
    return { splitsPerformed, nestedInsBefore, nestedInsAfter, splitDetails };
  }

  /**
   * Create a deletion element (not inside original w:ins)
   */
  private static createDeletion(
    op: DiffOperation,
    textMap: TextPositionMap[],
    newPara: Element,
    xmlDoc: Document,
    authorName: string,
    date: string,
    changeId: number
  ): number {
    const delElem = xmlDoc.createElementNS(this.W_NAMESPACE, 'w:del');
    delElem.setAttribute('w:id', String(changeId));
    delElem.setAttribute('w:author', authorName);
    delElem.setAttribute('w:date', date);

    // Copy original runs for deleted text — use textMap for the actual characters
    // (op.text may have single-space joins while positions span real whitespace like tabs)
    for (let i = op.startPos; i < op.endPos; i++) {
      if (i >= textMap.length) break;
      
      const posMap = textMap[i];
      if (i === op.startPos || (i > op.startPos && textMap[i - 1].run !== posMap.run)) {
        const newRun = this.preserveFormatting(posMap.run, '');
        delElem.appendChild(newRun);
      }
      
      // Get the actual character from textMap instead of op.text to avoid length mismatches
      const lastRun = delElem.lastChild as Element;
      const textElem = this.getOrCreateTextElement(lastRun, xmlDoc);
      textElem.textContent = (textElem.textContent || '') + this.getCharFromMap(posMap);
    }
    
    newPara.appendChild(delElem);
    return changeId + 1;
  }

  /**
   * Add spaces before and after insertion text to prevent words from sticking together
   * @param insertionText - Text being inserted
   * @returns Text with spaces added before and after
   */
  private static addSpacingToInsertion(insertionText: string): string {
    let result = insertionText.trim();
    
    // Add space before if not already present
    if (result.length > 0 && !result.startsWith(' ')) {
      result = ' ' + result;
    }
    
    // Add space after if not already present
    if (result.length > 0 && !result.endsWith(' ')) {
      result = result + ' ';
    }
    
    return result;
  }

  /**
   * Create an insertion element (not inside original w:ins)
   */
  private static createInsertion(
    op: DiffOperation,
    textMap: TextPositionMap[],
    newPara: Element,
    xmlDoc: Document,
    authorName: string,
    date: string,
    changeId: number,
    generationMode: TrackChangesGenerationMode = 'redline-insert',
  ): number {
    const insElem = xmlDoc.createElementNS(this.W_NAMESPACE, 'w:ins');
    insElem.setAttribute('w:id', String(changeId));
    insElem.setAttribute('w:author', authorName);
    insElem.setAttribute('w:date', date);
    logSuggestEdit('[TrackChangeAuthor] w:ins element created', {
      changeId,
      authorName,
      parentTag: newPara.localName || newPara.tagName,
      parentAuthor:
        newPara.getAttribute('w:author') ||
        newPara.getAttributeNS('http://schemas.openxmlformats.org/wordprocessingml/2006/main', 'author') ||
        null,
    });

    // Determine formatting for insertion (inherit from adjacent runs)
    let referenceRun: Element | null = null;
    if (op.startPos > 0 && op.startPos <= textMap.length) {
      referenceRun = textMap[Math.min(op.startPos, textMap.length - 1)].run;
    } else if (textMap.length > 0) {
      referenceRun = textMap[0].run;
    }

    // Word-level AI redlines need padding; char-level simple-edit must not add spaces
    // (e.g. Hebrew הואיל → חדש glued to surrounding text).
    const spacedText =
      generationMode === 'simple-edit' ? op.text : this.addSpacingToInsertion(op.text);

    // Create run for inserted text
    const newRun = referenceRun ? this.preserveFormatting(referenceRun, spacedText) : this.createBasicRun(xmlDoc, spacedText);
    insElem.appendChild(newRun);
    
    newPara.appendChild(insElem);
    return changeId + 1;
  }

  /**
   * Get or create w:t element in a run
   */
  /**
   * Get the actual character from a textMap entry, handling <w:tab/> and <w:br/> elements.
   */
  private static getCharFromMap(posMap: TextPositionMap): string {
    if (posMap.textNode.nodeType === Node.ELEMENT_NODE) {
      const localName = this.getLocalName(posMap.textNode as Element);
      if (localName === 'tab') return '\t';
      if (localName === 'br') return '\n';
    }
    return posMap.textNode.textContent?.[posMap.offset] || '';
  }

  private static getOrCreateTextElement(run: Element, xmlDoc: Document): Element {
    // Look for existing w:t element
    for (const child of Array.from(run.childNodes)) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const elem = child as Element;
        if (this.isTag(elem, 't')) {
          return elem;
        }
      }
    }
    
    // Create new w:t element
    const textElem = xmlDoc.createElementNS(this.W_NAMESPACE, 'w:t');
    textElem.setAttribute('xml:space', 'preserve');
    run.appendChild(textElem);
    return textElem;
  }

  /**
   * Create basic run with text (no special formatting)
   */
  private static createBasicRun(xmlDoc: Document, text: string): Element {
    const run = xmlDoc.createElementNS(this.W_NAMESPACE, 'w:r');
    const textElem = xmlDoc.createElementNS(this.W_NAMESPACE, 'w:t');
    textElem.setAttribute('xml:space', 'preserve');
    textElem.textContent = text;
    run.appendChild(textElem);
    return run;
  }

  /**
   * Preserve formatting from original run to new run
   * @param originalRun - Original w:r element
   * @param newText - New text for the run
   * @returns New w:r element with copied formatting
   */
  private static preserveFormatting(originalRun: Element, newText: string): Element {
    // Create new run in the same document
    const xmlDoc = originalRun.ownerDocument;
    if (!xmlDoc) {
      logError('[RobustXmlTrackChangesGenerator] No owner document for original run');
      return originalRun.cloneNode(false) as Element;
    }

    const newRun = xmlDoc.createElementNS(this.W_NAMESPACE, 'w:r');
    
    // Copy attributes from original run
    for (let i = 0; i < originalRun.attributes.length; i++) {
      const attr = originalRun.attributes[i];
      newRun.setAttribute(attr.name, attr.value);
    }

    // Find and copy w:rPr (run properties) from original run
    let runProperties: Element | null = null;
    for (const child of Array.from(originalRun.childNodes)) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const elem = child as Element;
        if (this.isTag(elem, 'rPr')) {
          runProperties = elem.cloneNode(true) as Element;
          break;
        }
      }
    }

    // Add run properties to new run if they exist
    if (runProperties) {
      newRun.appendChild(runProperties);
    }

    // Add text element if text is provided
    if (newText) {
      const textElem = xmlDoc.createElementNS(this.W_NAMESPACE, 'w:t');
      textElem.setAttribute('xml:space', 'preserve');
      textElem.textContent = newText;
      newRun.appendChild(textElem);
    }

    return newRun;
  }

  /**
   * Helper: Get local name of element (handles namespaces)
   */
  private static getLocalName(element: Element): string {
    return element.localName || element.tagName.split(':').pop() || element.tagName;
  }

  /**
   * Helper: Check if element matches a tag name
   */
  private static isTag(element: Element, tagName: string): boolean {
    const localName = this.getLocalName(element);
    return localName === tagName || element.tagName === tagName || element.tagName.endsWith(`:${tagName}`);
  }
}

